import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InquiryModal({ open, onOpenChange, division }) {
  const form = division.form;
  const [values, setValues] = useState({});
  const [sending, setSending] = useState(false);
  const setValue = (key, value) => setValues((s) => ({ ...s, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const field of form.fields) {
      if (field.required && !String(values[field.key] || "").trim()) {
        toast.error(`Completa el campo: ${field.label}`);
        return;
      }
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, {
        division: division.slug,
        name: values.name || null,
        email: values.email || "",
        phone: values.phone || null,
        organization: values.organization || null,
        service: values.service || null,
        details: values.details || "",
      });
      toast.success("Solicitud enviada", {
        description: "Nuestro equipo te contactará a la brevedad.",
      });
      setValues({});
      onOpenChange(false);
    } catch (err) {
      toast.error("No pudimos enviar tu solicitud", {
        description: "Inténtalo de nuevo en unos minutos.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-line bg-ink text-slate-200 sm:max-w-lg"
        data-testid={form.modalTestId}
      >
        <DialogHeader>
          <p
            className="font-mono text-[11px] uppercase tracking-[0.28em]"
            style={{ color: division.accent }}
          >
            {division.name}
          </p>
          <DialogTitle className="font-serif text-3xl font-semibold text-slate-100">
            {form.title}
          </DialogTitle>
          <DialogDescription className="text-sm font-light text-slate-400">
            {form.subtitle}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          {form.fields.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                {field.label}
                {field.required ? " *" : ""}
              </Label>
              {field.type === "textarea" ? (
                <Textarea
                  data-testid={field.testid}
                  value={values[field.key] || ""}
                  onChange={(e) => setValue(field.key, e.target.value)}
                  rows={4}
                  className="resize-none border-line bg-void/60 text-slate-200 placeholder:text-slate-600 focus-visible:ring-gold/50"
                />
              ) : field.type === "select" ? (
                <Select
                  value={values[field.key] || ""}
                  onValueChange={(v) => setValue(field.key, v)}
                >
                  <SelectTrigger
                    data-testid={field.testid}
                    className="border-line bg-void/60 text-slate-200"
                  >
                    <SelectValue placeholder="Selecciona una opción" />
                  </SelectTrigger>
                  <SelectContent className="border-line bg-ink text-slate-200">
                    {field.options.map((opt) => (
                      <SelectItem key={opt} value={opt} data-testid={`${field.testid}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  data-testid={field.testid}
                  type={field.type}
                  value={values[field.key] || ""}
                  onChange={(e) => setValue(field.key, e.target.value)}
                  className="border-line bg-void/60 text-slate-200 placeholder:text-slate-600 focus-visible:ring-gold/50"
                />
              )}
            </div>
          ))}

          <div className="flex items-center gap-2 pt-1 text-[11px] text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" style={{ color: division.accent }} />
            Tu información se maneja con total confidencialidad.
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid={form.submitTestId}
            className="group inline-flex w-full items-center justify-center gap-3 px-8 py-4 font-mono text-xs uppercase tracking-[0.24em] text-void transition-opacity duration-300 hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: division.accent }}
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            )}
            {sending ? "Enviando..." : "Enviar solicitud"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
